/**
 * Nexus Admin Layout
 * Wrapper component for admin pages with sidebar navigation
 */

import { useEffect } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useNexusStore } from '@/store/nexusStore'

const navItems = [
  { path: '/nexus/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/nexus/users', label: 'Users', icon: '👥' },
  { path: '/nexus/abilities', label: 'Abilities', icon: '⚡' },
  { path: '/nexus/api-services', label: 'API Services', icon: '🔌' },
  { path: '/nexus/revenue', label: 'Revenue', icon: '💰' },
]

export default function NexusLayout() {
  const navigate = useNavigate()
  const { admin, isAuthenticated, isLoading, fetchAdmin, logout } = useNexusStore()

  useEffect(() => {
    fetchAdmin()
  }, [fetchAdmin])

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/nexus/login')
    }
  }, [isLoading, isAuthenticated, navigate])

  const handleLogout = async () => {
    await logout()
    navigate('/nexus/login')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-800 border-r border-slate-700 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-purple-600 flex items-center justify-center">
              <span className="text-lg font-bold text-white">N</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Nexus</h1>
              <p className="text-xs text-gray-400">Admin Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                    : 'text-gray-400 hover:bg-slate-700 hover:text-white'
                }`
              }
            >
              <span className="text-lg">{item.icon}</span>
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-purple-600 flex items-center justify-center">
              <span className="text-sm font-bold text-white">
                {admin?.name?.charAt(0) || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {admin?.name || 'Admin'}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {admin?.email}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-slate-700 rounded-lg transition-all"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}
